import React from 'react';
import PropTypes from 'prop-types';
import { areArraysEqual } from 'Utils';
import { ElementWithAnswerQuestionTooltip } from 'SelfServeRecipientBuilder/components';
import CalendarField from './CalendarField';
import LineBreak from './LineBreak';
import MultiSelectCheckList from './MultiSelectCheckList';
import NoteWrapper from './NoteWrapper';
import RadioButton from './RadioButton';
import SelectList from './SelectList';
import Subheading from './Subheading';
import TextAreaField from './TextAreaField';
import TextField from './TextField';

const FIRST_FIELD_INDEX = 0;
class StaticBuilderFormField extends React.Component {
  constructor(props) {
    super(props);
    this.getOptionsWithDefaults = this.getOptionsWithDefaults.bind(this);
    this.addBlankChoiceToStateOptions = this.addBlankChoiceToStateOptions.bind(this);
  }

  getOptionsWithDefaults(isChecked) {
    const { field } = this.props;
    const newOptions = field.options.slice();
    for (let i = 0; i < newOptions.length; i += 1) {
      if (isChecked(newOptions[i])) {
        newOptions[i].defaultChecked = true;
      }
    }
    return newOptions;
  }

  addBlankChoiceToStateOptions() {
    // SmartyStreets will stupidly add a default choice to state dropdown if it doesn't have
    // one. This isn't needed so our code that enables SmartyStreets removes it, but since
    // SmartyStreets is not React aware it will break the current selection and force the dropdown
    // to select Alabama.  If we preemptively add our own blank choice this problem won't happen.

    // If the first three options are the first three states alphabetically we can be confident
    // that it is in fact a state list
    const { id, options } = this.props.field;
    const firstOptions = options.map(option => option.text).slice(0, 3);
    const hasStateOptions = areArraysEqual(firstOptions, ['Alabama', 'Alaska', 'Arizona']) ||
                            areArraysEqual(firstOptions, ['AL', 'AK', 'AZ']);
    if (id.includes('state') && hasStateOptions) {
      return [{ text: '', value: '' }, ...options];
    }
    return options;
  }

  render() {
    const {
      field,
      fieldNumber,
      setCurrentFieldValue,
    } = this.props;

    const commonProps = {
      id: field.id,
      label: field.label,
      info: field.info,
      isSelfServe: field.isSelfServe,
      setCurrentFieldValue,
    };
    let fieldComponent = null;
    switch (field.type) {
      case 'text_field':
        fieldComponent = <TextField {...commonProps} initialValue={field.initialValue} />;
        break;
      case 'hr':
        fieldComponent = <LineBreak {...commonProps} />;
        break;
      case 'note':
        fieldComponent = <NoteWrapper {...commonProps} />;
        break;
      case 'subheading':
        fieldComponent = <Subheading {...commonProps} />;
        break;
      case 'calendar':
        fieldComponent = <CalendarField {...commonProps} initialValue={field.initialValue} />;
        break;
      case 'calendar_month':
        fieldComponent = (
          <CalendarField
            {...commonProps}
            datepickerType="month"
            initialValue={field.initialValue}
          />
        );
        break;
      case 'radio':
        fieldComponent = (
          <RadioButton
            {...commonProps}
            getOptionsWithDefaults={this.getOptionsWithDefaults}
            options={field.options}
            initialValue={field.initialValue}
          />
        );
        break;
      case 'select':
        fieldComponent = (
          <SelectList
            {...commonProps}
            options={this.addBlankChoiceToStateOptions()}
            initialValue={field.initialValue}
          />
        );
        break;
      case 'select_multiple':
        fieldComponent = (
          <MultiSelectCheckList
            {...commonProps}
            getOptionsWithDefaults={this.getOptionsWithDefaults}
            options={field.options}
            initialSelectedValues={field.initialValues}
          />
        );
        break;
      case 'text_area':
      case 'textarea':
        fieldComponent = <TextAreaField {...commonProps} initialValue={field.initialValue} />;
        break;
      default:
        fieldComponent = null;
    }

    const isFirstField = fieldNumber === FIRST_FIELD_INDEX;
    if (isFirstField) {
      // Check for fieldNumber for the Self Serve demo case.
      // The automatically opened preview modal contains 2 fields by default
      // We only want to attach the answer question tooltip to the first field
      fieldComponent = (
        <ElementWithAnswerQuestionTooltip
          targetElementType={field.type}
        >
          {fieldComponent}
        </ElementWithAnswerQuestionTooltip>
      );
    }

    return (
      <div key={field.id} className="control-group" id={`group_${field.id}`}>
        {fieldComponent}
      </div>
    );
  }
}

StaticBuilderFormField.defaultProps = {
  fieldNumber: 0,
  isSelfServe: false,
  setCurrentFieldValue: () => {},
};

StaticBuilderFormField.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
  }).isRequired,
  fieldNumber: PropTypes.number,
  setCurrentFieldValue: PropTypes.func,
};

export default StaticBuilderFormField;



// WEBPACK FOOTER //
// ./src-react/StaticBuilder/StaticBuilderFormField.jsx